<template>
  <nav class="sticky top-0 z-50 backdrop-blur-md transition-colors duration-300 bg-white/90 dark:bg-dark-surface/90 border-b border-gray-200 dark:border-secondary">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <div class="flex-shrink-0">
          <a href="#hero" class="flex items-center" aria-label="Miracle Cloud Home">
            <img class="h-8 w-auto" src="@/assets/img/logo.png" alt="Miracle Cloud" />
          </a>
        </div>

        <!-- Desktop Menu -->
        <div class="hidden md:block">
          <div class="ml-10 flex items-baseline space-x-4">
            <a
              v-for="item in navItems"
              :key="item.id"
              :href="'#' + item.id"
              class="px-3 py-2 rounded-md text-sm font-medium transition-colors"
              :class="activeSection === item.id || (item.id === 'hero' && !activeSection) ? 'text-accent dark:text-accent-dark' : 'text-text-secondary hover:text-primary dark:text-dark-text-secondary dark:hover:text-dark-text'"
            >
              {{ t(`nav.${item.key}`) }}
            </a>
          </div>
        </div>

        <!-- Right Side: Dark Mode & Lang Switcher -->
        <div class="hidden md:flex items-center space-x-4">
          <!-- Dark Mode Toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2 rounded-full text-text-secondary hover:text-primary dark:text-dark-text-secondary dark:hover:text-dark-text focus:outline-none transition-colors"
            aria-label="Toggle Dark Mode"
          >
            <Sun v-if="isDark" class="h-5 w-5" />
            <Moon v-else class="h-5 w-5" />
          </button>

          <!-- Language Switcher -->
          <div class="relative flex items-center group cursor-pointer text-sm font-medium text-text-secondary dark:text-dark-text-secondary hover:text-primary dark:hover:text-dark-text transition-colors">
            <Globe class="h-5 w-5 mr-1" />
            <span>{{ locale === 'zh-TW' ? '中文' : 'EN' }}</span>
            <div class="absolute right-0 top-full mt-2 w-32 bg-white dark:bg-dark-surface border border-gray-200 dark:border-secondary rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 overflow-hidden">
              <button
                @click="setLanguage('zh-TW')"
                class="block w-full text-left px-4 py-2 text-sm text-text-main dark:text-dark-text hover:bg-gray-100 dark:hover:bg-secondary transition-colors"
                :class="{ 'font-bold text-accent dark:text-accent-dark': locale === 'zh-TW' }"
              >
                中文 (繁體)
              </button>
              <button
                @click="setLanguage('en')"
                class="block w-full text-left px-4 py-2 text-sm text-text-main dark:text-dark-text hover:bg-gray-100 dark:hover:bg-secondary transition-colors"
                :class="{ 'font-bold text-accent dark:text-accent-dark': locale === 'en' }"
              >
                English
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Button -->
        <div class="flex md:hidden">
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="inline-flex items-center justify-center p-2 rounded-md text-text-secondary hover:text-primary dark:text-dark-text-secondary dark:hover:text-dark-text focus:outline-none transition-colors"
            aria-label="Open main menu"
          >
            <Menu v-if="!mobileMenuOpen" class="block h-6 w-6" />
            <X v-else class="block h-6 w-6" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div
      v-show="mobileMenuOpen"
      class="md:hidden border-t border-gray-200 dark:border-secondary bg-white dark:bg-dark-surface shadow-lg"
    >
      <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
        <a
          v-for="item in navItems"
          :key="item.id"
          :href="'#' + item.id"
          @click="mobileMenuOpen = false"
          class="block px-3 py-2 rounded-md text-base font-medium transition-colors"
          :class="activeSection === item.id || (item.id === 'hero' && !activeSection) ? 'text-accent dark:text-accent-dark bg-gray-50 dark:bg-secondary/50' : 'text-text-secondary hover:text-primary hover:bg-gray-50 dark:text-dark-text-secondary dark:hover:text-dark-text dark:hover:bg-secondary/50'"
        >
          {{ t(`nav.${item.key}`) }}
        </a>
      </div>
      <div class="pt-4 pb-3 border-t border-gray-200 dark:border-secondary">
        <div class="flex items-center justify-between px-5">
          <!-- Mobile Lang Switcher -->
          <div class="flex space-x-2">
            <button
              @click="setLanguage('zh-TW'); mobileMenuOpen = false"
              class="px-3 py-1 rounded-md text-sm font-medium transition-colors"
              :class="locale === 'zh-TW' ? 'bg-accent/10 text-accent dark:bg-accent-dark/10 dark:text-accent-dark' : 'text-text-secondary dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-secondary'"
            >
              中文
            </button>
            <button
              @click="setLanguage('en'); mobileMenuOpen = false"
              class="px-3 py-1 rounded-md text-sm font-medium transition-colors"
              :class="locale === 'en' ? 'bg-accent/10 text-accent dark:bg-accent-dark/10 dark:text-accent-dark' : 'text-text-secondary dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-secondary'"
            >
              EN
            </button>
          </div>
          <!-- Mobile Dark Mode Toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2 rounded-full text-text-secondary hover:text-primary dark:text-dark-text-secondary dark:hover:text-dark-text focus:outline-none transition-colors"
            aria-label="Toggle Dark Mode"
          >
            <Sun v-if="isDark" class="h-6 w-6" />
            <Moon v-else class="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useScrollSpy } from '@/composables/useScrollSpy'
import { useDarkMode } from '@/composables/useDarkMode'
import { Menu, X, Sun, Moon, Globe } from 'lucide-vue-next'

const { t, locale } = useI18n({ useScope: 'global' })

const navItems = [
  { id: 'hero', key: 'home' },
  { id: 'services', key: 'services' },
  { id: 'whyUs', key: 'whyUs' },
  { id: 'solutions', key: 'solutions' },
  { id: 'caseStudies', key: 'caseStudies' },
  { id: 'faq', key: 'faq' },
  { id: 'contact', key: 'contact' }
]

const { activeSection } = useScrollSpy(['services', 'whyUs', 'solutions', 'caseStudies', 'faq', 'contact'])
const { isDark, toggleDarkMode } = useDarkMode()

const mobileMenuOpen = ref(false)

function setLanguage(lang) {
  locale.value = lang
  localStorage.setItem('locale', lang)
}
</script>
